'use client'
import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next"

/**
 * Props for `HomePageAddressCheckerPanel`.
 */
export type HomePageAddressCheckerPanelProps =
  SliceComponentProps<Content.HomePageAddressCheckerPanelSlice>;

/**
 * Component for "HomePageAddressCheckerPanel" Slices.
 */
const HomePageAddressCheckerPanel: FC<HomePageAddressCheckerPanelProps> = ({ slice }) => {
  const tokenKey = "tfa"

  const slickRef = useRef(null) as any
  const resultRef = useRef(null) as any
  const inputArea = useRef(null) as any

  const [searching, setSearching] = useState(false);
  const [addressList, setAddressList] = useState([]) as any
  const [tokenValid, setTokenValid] = useState(false);

  const readToken = () => {
    if (typeof window === "undefined") return null;

    const rawToken = window.localStorage.getItem(tokenKey);
    if (!rawToken) return null;

    try {
      return JSON.parse(rawToken) as { expire: number; response: any };
    } catch {
      return null;
    }
  }

  const writeToken = (response: any) => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(tokenKey, JSON.stringify({
      expire: Date.now() + 3600 * 1000,
      response,
    }));
  }
  

  const getToken = useCallback(async() => {
    return await fetch("/api/address/auth").then(res => res.json())
    .then(response => {setTokenValid(true);
                      writeToken(response)
                      return response
                    })
    .catch(error => {
      console.error('Error:', error)
      return null
    });
  }, [])

  const getAddress = async (e: any) =>{
    setSearching(true)
    let token = readToken() as any
    if (!token?.response) {
      const refreshedToken = await getToken()
      token = refreshedToken ? { response: refreshedToken } : null
    }

    if (!token?.response) {
      setSearching(false)
      return
    }

    const address = e.target.value;
    resultRef?.current?.classList.remove("hide"); 
    await fetch("/api/address", {method:"post", body: JSON.stringify({address: address, token: token.response})}).then(res => res.json())
    .then(response => {setAddressList(response)} )
    .catch(error => console.error('Error:', error));
    setSearching(false)
  }

  useEffect(()=>{
    const token = readToken();
    if (!token || Date.now() > token.expire) {
      getToken();
    } else {
      setTokenValid(true);
    }
  }, [getToken])

  const clearAddress = () =>{
    inputArea.current.value = ""; 
    resultRef.current.classList.remove("hide"); 
    slickRef.current.slickGoTo(0)
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-jade font-fellix"
    >
      <div className="bg-lightergrey absolute w-full h-38 md:h-44 -top-12 -z-1"></div>
        <div className="max-w-[550px] mx-auto relative p-4 ">
          <h2 className="w-full text-center">{slice.primary.title}</h2>
          <input className="relative z-10 rounded-full p-4 bg-lightgrey drop-shadow-lg w-full block mt-6 text-center text-[19px] leading-[19px] placeholder:text-jade outline-none appearance-none" type="text" placeholder={tokenValid === true ? "Enter your address" : "Loading..."} onChange={(e: any)=>getAddress(e)} ref={inputArea}/>
            {searching === true ?
            <div className="flex items-center justify-center h-full absolute right-6 top-14 z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-orange border-gray-200"></div>
            </div>
            : null }
          {addressList?.hits?.length > 0 ?
              <div className="pb-4 bg-white border-jade/10 border rounded-b-[20px] md:w-full -mt-8 z-9 pt-10 drop-shadow absolute right-4 left-4 md:right-0 md:left-0" ref={resultRef}>
                <ul className="text-[14px] leading-3.5">
                  {addressList?.hits?.map((item: any, index: number)=>{
                    return (
                      <Fragment  key={index} >
                      {item.placeId ? 
                      <li className="p-4 border-b border-b-jade/10 cursor-pointer hover:text-orange" onClick={()=>{window.location.href="/address-checker?address="+item.displayAddress+"&placeId="+item.placeId+"&tui="+item.tui}}>{item.displayAddress}</li>
                      : <li className="p-4 border-b border-b-jade/10 cursor-pointer hover:text-orange" onClick={()=>{window.location.href="/address-checker?address="+item.displayAddress+"&tui="+item.tui}}>{item.displayAddress}</li>}
                      </Fragment>
                    )
                  })}
                </ul>
              </div>
          : null}
          {addressList?.hits?.length < 1 && inputArea.current?.value !== "" ?
              <div className="pb-4 bg-white border-jade/10 border rounded-b-[20px] -mt-6 w-full z-9 pt-10 drop-shadow px-4 absolute" ref={resultRef}>
                <ul >
                  <li>Sorry that address isn&apos;t in our service area.<br/>Doesn&apos;t look right? Click <a href={"/form-address-details-check"} style={{color: 'blue'}}>here</a></li>
                </ul>
              </div>
          : null}
          {searching !== true ?
            <p className="clear-address" onClick={()=>clearAddress()}></p>
          : null }
        </div>
        <div className="grid grid-cols-12 md:flex justify-between gap-y-8 gap-x-4 md:gap-0 max-w-[700px] mx-auto mt-12 p-4 md:p-0">
          {slice.primary.panels?.map((item: any, index: number) => (
            <Fragment key={index}>
            {index === 3 && slice.primary.panels && slice.primary.panels.length % 6 !== 0 ? (<div className="col-span-2 md:hidden"></div>) : null}
            <div className="col-span-4 text-center w-full max-w-[100px]" key={index}>
              <a href={item.link.uid ? item.link.uid : item.link?.url}>
                <PrismicNextImage className="block mx-auto w-[50px] md:w-auto" field={item.icon} fallbackAlt="" />
                <p className="font-semibold pt-3 text-[16px] md:text-[18px] leading-[22px]">{item.title}</p>
              </a>
            </div>
            </Fragment>
          ))}
        </div>
    </section>
  );
};

export default HomePageAddressCheckerPanel;
